import { useEffect, useRef, useState, type ReactNode } from "react";
import { CATEGORIES, categoryLabelAr } from "../lib/categories";
import { SECTION_LABEL, isArticleSection, sectionSlots } from "../lib/sections";
import type { BlockLayout, NewsletterSection, SectionArticle } from "../lib/segment-types";
import { LayoutPicker } from "./LayoutPicker";
import { ArticlePicker } from "./ArticlePicker";
import { ArticleRow, AutoSummary } from "./ArticleRow";
import { SectionIcon } from "./SectionIcon";
import { Bidi } from "./primitives";
import {
  IconGrip, IconImage, IconLink, IconMore, IconParagraph, IconPencil, IconSearch, IconSettings, IconWarning,
} from "./icons";

const ACTION_ICON = {
  pick: IconSearch,
  settings: IconSettings,
  edit: IconPencil,
  image: IconImage,
  link: IconLink,
  text: IconParagraph,
} as const;

/**
 * One section of the newsletter, shown as the reader will see it.
 *
 * The default state is a preview, not a form: an editor arranging an issue is
 * judging what sits next to what. Settings — where the articles come from, how
 * many, the heading — live behind the row's menu, so a column of sections
 * reads as the newsletter rather than as ten stacked forms.
 */
export function SectionCard({
  section, audience, locked, onChange, onRemove, usedInIssue = [], defaultOpen, muted,
  note, canComment, onRequestChanges, onResolveComment,
}: {
  section: NewsletterSection;
  /** Structural furniture rather than content — shown quieter. */
  muted?: boolean;
  /** A section the editor has just added opens ready to be filled in. */
  defaultOpen?: boolean;
  audience: string[];
  /** Every article URL already used anywhere in this newsletter. */
  usedInIssue?: string[];
  locked?: boolean;
  onChange: (patch: Partial<NewsletterSection>) => void;
  onRemove: () => void;
  note?: { id: string; text: string } | null;
  canComment?: boolean;
  onRequestChanges?: (comment: string, sectionId: string) => void;
  onResolveComment?: (commentId: string) => void;
}) {
  const [picking, setPicking] = useState(false);
  const [open, setOpen] = useState(Boolean(defaultOpen)); // settings drawer
  const [menu, setMenu] = useState(false);
  const [focusField, setFocusField] = useState<string | null>(null);
  const [justAdded, setJustAdded] = useState<string | null>(null);
  const settingsRef = useRef<HTMLDivElement>(null);
  const [comment, setComment] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menu) return;
    const onDoc = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenu(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setMenu(false); };
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("keydown", onKey);
    };
  }, [menu]);

  // Put the cursor in the box the action named, once the drawer has rendered.
  useEffect(() => {
    if (!open || !focusField) return;
    const el = settingsRef.current?.querySelector<HTMLElement>(`[data-field="${focusField}"]`);
    el?.focus();
    if (el && "select" in el) (el as HTMLInputElement).select();
    setFocusField(null);
  }, [open, focusField]);

  const articles = section.articles ?? [];
  // A picture read from an article's own page arrives after it was added, so
  // the patch has to apply to the list as it is then, not as it was.
  const articlesRef = useRef(articles);
  articlesRef.current = articles;
  const slots = sectionSlots(section);
  const dynamicSlots = Math.max(0, slots - articles.length);
  const followsSubscriber = !section.category;
  const pickerCategory = section.category ?? audience[0] ?? null;

  const setArticles = (next: SectionArticle[]) => onChange({ articles: next });
  const moveArticle = (i: number, dir: -1 | 1) => {
    const next = [...articles];
    const t = i + dir;
    if (t < 0 || t >= next.length) return;
    [next[i], next[t]] = [next[t], next[i]];
    setArticles(next);
  };

  // What is still missing before this section can be sent. A freshly added
  // Image or CTA section used to render as an empty strip with no way in —
  // the fields were real, but behind the ⋯ menu where nobody looked.
  const missing = setupNeeded(section);

  const headingFallback =
    section.type === "category" ? (audience[0] ? categoryLabelAr(audience[0]) : "التغذية")
    : section.type === "banner" ? "Banner headline"
    : "";

  return (
    <div className={`nl-sec${note ? " has-comment" : ""}${open ? " is-open" : ""}${muted ? " is-muted" : ""}`}>
      <div className="nl-sec-main">
        <SectionIcon type={section.type} />

        <div className="nl-sec-body">
          <div className="nl-sec-label">{SECTION_LABEL[section.type]}</div>

          {/* ── The section as a reader would meet it ──────────────────── */}
          {section.type === "category" && (
            <>
              <div className="nl-sec-catname"><Bidi>{section.heading || headingFallback}</Bidi></div>
              <span className="nl-sec-chip">
                {followsSubscriber ? "Follows each subscriber" : categoryLabelAr(section.category!)}
              </span>
            </>
          )}

          {section.type === "featured" && (
            articles[0]
              ? <ArticleRow
                  article={articles[0]} index={0} count={1} locked={locked}
                  onRemove={() => setArticles([])}
                  defaultOpen={articles[0].id === justAdded}
                  onChange={(patch) => setArticles([{ ...articles[0], ...patch }])}
                />
              : dynamicSlots > 0
                ? <AutoSummary count={1} category={followsSubscriber ? null : section.category!} />
                : <div className="nl-sec-copy is-empty">No link chosen yet</div>
          )}

          {/* Pinned articles are real information and are shown one by one.
              The rest are one line, because repeating it per slot said the
              same sentence three or six times over. */}
          {section.type === "articles" && (
            <div className="nl-sec-rows">
              {articles.map((a, i) => (
                <ArticleRow
                  key={a.id} article={a} index={i} count={articles.length} locked={locked}
                  onMove={(d) => moveArticle(i, d)}
                  onRemove={() => setArticles(articles.filter((x) => x.id !== a.id))}
                  defaultOpen={a.id === justAdded}
                  onChange={(patch) => setArticles(
                    articles.map((x) => (x.id === a.id ? { ...x, ...patch } : x)),
                  )}
                />
              ))}
              {articles.length === 0 && dynamicSlots === 0 && (
                <div className="nl-sec-copy is-empty">No links added yet</div>
              )}
              <AutoSummary
                count={dynamicSlots}
                category={followsSubscriber ? null : section.category!}
                hasPinned={articles.length > 0}
              />
            </div>
          )}

          {section.type === "banner" && (
            missing
              ? <SetupPrompt label={missing} disabled={locked} onClick={() => setOpen(true)} />
              : <div className="nl-sec-banner">
                  {section.image
                    ? <ImagePreview src={section.image} alt="" bare />
                    : <div className="nl-sec-bannerfill">
                        <span><Bidi>{section.heading || "رحلتك نحو حياة أكثر صحة"}</Bidi></span>
                        {section.text && <em><Bidi>{section.text}</Bidi></em>}
                        <span className="nl-sec-bannercta">{section.cta_label || "اكتشف المزيد"}</span>
                      </div>}
                </div>
          )}

          {section.type === "block" && (
            <BlockPreview section={section} missing={missing} />
          )}

          {section.type === "image" && (
            missing
              ? <SetupPrompt label={missing} disabled={locked} onClick={() => setOpen(true)} />
              : <ImagePreview src={section.image!} alt={section.text ?? ""} />
          )}

          {(section.type === "tip" || section.type === "doctor" || section.type === "text") && (
            missing
              ? <SetupPrompt label={missing} disabled={locked} onClick={() => setOpen(true)} />
              : <div className="nl-sec-copy"><Bidi>{section.text}</Bidi></div>
          )}

          {section.type === "cta" && (
            <div className="nl-sec-ctarow">
              <span className="nl-sec-ctachip">{section.cta_label || "اعرف المزيد"}</span>
              {missing && <SetupPrompt label={missing} disabled={locked} onClick={() => setOpen(true)} />}
            </div>
          )}

          {section.type === "divider" && <div className="nl-sec-rule" aria-hidden />}
          {section.type === "spacer" && <div className="nl-sec-gap" aria-hidden />}

          {section.type === "footer" && (
            <div className="nl-sec-copy">Altibbi logo, social links and unsubscribe</div>
          )}
        </div>

        <div className="nl-sec-tools">
          {missing && <span className="nl-sec-todo">Empty — won’t be sent</span>}
          {!locked && <span className="nl-sec-grip" aria-hidden title="Drag to reorder"><IconGrip /></span>}
          <div className="nl-sec-menuwrap" ref={menuRef}>
            <button
              type="button" className="nl-sec-menubtn"
              aria-haspopup="menu" aria-expanded={menu}
              aria-label={`${SECTION_LABEL[section.type]} options`}
              onClick={() => setMenu((v) => !v)}
            ><IconMore /></button>
            {menu && (
              <div className="nl-sec-menu" role="menu">
                {section.type !== "divider" && section.type !== "spacer" && section.type !== "footer" && (
                  <button type="button" role="menuitem" onClick={() => { setMenu(false); setOpen((v) => !v); }}>
                    {open ? "Hide settings" : "Edit settings"}
                  </button>
                )}
                {isArticleSection(section.type) && !locked && (
                  <button type="button" role="menuitem" onClick={() => { setMenu(false); setPicking(true); }}>
                    Choose an article
                  </button>
                )}
                {canComment && onRequestChanges && (
                  <button type="button" role="menuitem" onClick={() => { setMenu(false); setComment(""); }}>
                    Comment
                  </button>
                )}
                {!locked && (
                  <button type="button" role="menuitem" className="is-danger" onClick={() => { setMenu(false); onRemove(); }}>
                    Remove section
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Position is changed often and is quicker to point at than to name,
          so the arrangement sits on the card rather than in the drawer. */}
      {!locked && section.type === "block" && (
        <div className="nl-sec-layout">
          <LayoutPicker
            value={((section.layout as BlockLayout) ?? "stacked")}
            onChange={(layout) => onChange({ layout })}
          />
        </div>
      )}

      {/* The actions were all behind the ⋯ menu, so "how do I add a photo"
          had no visible answer. Each card now names what it can take. */}
      {!locked && (
        <div className="nl-sec-actions">
          {sectionActions(section).map((a) => (
            <button
              key={a.label}
              type="button"
              className="nl-sec-action"
              onClick={() => {
                if (a.does === "pick") { setPicking(true); return; }
                setOpen(true);
                if (a.field) setFocusField(a.field);
              }}
            >
              {(() => { const Icon = ACTION_ICON[a.icon]; return <Icon />; })()}
              {a.label}
            </button>
          ))}
        </div>
      )}

      {note && (
        <div className="nl-block-comment">
          <strong><IconWarning /> Editor comment</strong>
          <p>{note.text}</p>
          {onResolveComment && !locked && (
            <button type="button" className="nl-section-pick" onClick={() => onResolveComment(note.id)}>Resolve</button>
          )}
        </div>
      )}

      {comment !== null && onRequestChanges && (
        <div className="nl-block-comment">
          <textarea
            className="nl-textarea" rows={2}
            placeholder="What should change in this section?"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          <div className="nl-action-row">
            <button type="button" className="nl-section-pick" onClick={() => setComment(null)}>Cancel</button>
            <button
              type="button" className="nl-picker-add is-primary"
              disabled={!comment.trim()}
              onClick={() => { onRequestChanges(comment, section.id); setComment(null); }}
            >Request change</button>
          </div>
        </div>
      )}

      {/* ── Settings, out of the way until asked for ────────────────────── */}
      {open && (
        <div className="nl-sec-settings" ref={settingsRef}>
          {section.type !== "divider" && section.type !== "spacer" && section.type !== "footer" && (
            <label className="nl-field">
              <span>{section.type === "category" ? "Heading" : section.type === "block" ? "Heading" : "Section heading"}</span>
              <input
                className="nl-input" dir="auto" disabled={locked}
                data-field="heading"
                placeholder={headingFallback || "Optional"}
                value={section.heading ?? ""}
                onChange={(e) => onChange({ heading: e.target.value })}
              />
            </label>
          )}

          {(isArticleSection(section.type) || section.type === "category") && (
            <div className="nl-section-source">
              <label>
                <span>Category</span>
                <select
                  className="nl-builder-select" disabled={locked}
                  data-field="category"
                value={section.category ?? ""}
                  onChange={(e) => onChange({ category: e.target.value || null })}
                >
                  <option value="">The subscriber’s own category</option>
                  {CATEGORIES.map((c) => (
                    <option key={c.id} value={c.label}>{c.label} · {c.labelAr}</option>
                  ))}
                </select>
              </label>
              {(section.type === "articles" || section.type === "featured") && (
                <label>
                  {/* Filling from the category is an option on top of the
                      links you added, not the thing the section does. */}
                  <span>Add automatically</span>
                  <select
                    className="nl-builder-select" disabled={locked}
                    value={String(section.count ?? 0)}
                    onChange={(e) => onChange({ count: Number(e.target.value) })}
                  >
                    <option value="0">None — only the links I add</option>
                    {(section.type === "featured" ? [1] : [1, 2, 3, 4, 5, 6]).map((n) => (
                      <option key={n} value={n}>
                        {section.type === "featured"
                          ? "One from the reader’s category"
                          : `${n} more from the reader’s category`}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>
          )}

          {(section.type === "tip" || section.type === "text" || section.type === "banner" || section.type === "doctor" || section.type === "block") && (
            // Every other control here is labelled; this one was a bare box
            // sitting under the heading field with nothing to say what it was.
            <SettingField
              label={section.type === "banner" ? "Supporting line" : "Description"}
              hint={section.type === "block" ? "The paragraph under the heading." : undefined}
            >
              <textarea
                className="nl-textarea" dir="auto" rows={section.type === "text" ? 4 : 2}
                disabled={locked}
                data-field="text"
                placeholder={section.type === "banner" ? "Optional" : "Write the copy"}
                value={section.text ?? ""}
                onChange={(e) => onChange({ text: e.target.value })}
              />
            </SettingField>
          )}

          {/* Photo. Spelled out, because "how do I add a picture" was the
              question this card could not answer. */}
          {(section.type === "image" || section.type === "banner" || section.type === "block") && (
            <SettingField
              label="Photo"
              hint="Paste the address of an image already published on the web — it must start with https:// and end in .jpg, .png or .webp. 1200 × 600 or wider looks best in email."
            >
              <input
                className="nl-input" disabled={locked} type="url" inputMode="url"
                data-field="image"
                placeholder="https://cdn.altibbi.com/banner.jpg"
                value={section.image ?? ""}
                onChange={(e) => onChange({ image: e.target.value })}
              />
            </SettingField>
          )}

          {section.type === "image" && (
            <SettingField label="Describe the photo" hint="Read aloud by screen readers, and shown when a mail client blocks images.">
              <input
                className="nl-input" dir="auto" disabled={locked}
                placeholder="A doctor talking with a patient"
                value={section.text ?? ""}
                onChange={(e) => onChange({ text: e.target.value })}
              />
            </SettingField>
          )}

          {(section.type === "banner" || section.type === "cta" || section.type === "doctor" || section.type === "block") && (
            <div className="nl-section-source">
              <label>
                <span>Button label</span>
                <input
                  className="nl-input" dir="auto" disabled={locked}
                  data-field="cta_label"
                placeholder="اعرف المزيد"
                  value={section.cta_label ?? ""}
                  onChange={(e) => onChange({ cta_label: e.target.value })}
                />
              </label>
              <label style={{ flex: 1 }}>
                <span>Where the button goes</span>
                <input
                  className="nl-input" disabled={locked} type="url" inputMode="url"
                  data-field="url"
                placeholder="https://altibbi.com/…"
                  value={section.url ?? ""}
                  onChange={(e) => onChange({ url: e.target.value })}
                />
              </label>
            </div>
          )}

          {(section.type === "banner" || section.type === "cta" || section.type === "doctor" || section.type === "block") && (
            <p className="nl-field-note">
              The link is checked before sending — a missing or non-https address
              fails the pre-send check.
            </p>
          )}
        </div>
      )}

      {picking && (
        <ArticlePicker
          category={pickerCategory}
          onEnrich={(id, patch) => {
            // Arrives after the article's own page has been read.
            if (section.type === "block") {
              onChange({ image: section.image?.trim() ? section.image : patch.image });
              return;
            }
            setArticles(
              articlesRef.current.map((x: SectionArticle) =>
                x.id === id ? { ...x, ...patch, image: x.image?.trim() ? x.image : patch.image } : x,
              ),
            );
          }}
          used={usedInIssue.length ? usedInIssue : articles.map((a) => a.url)}
          allowFeatured={section.type === "featured"}
          onClose={() => setPicking(false)}
          onAdd={(article, asFeatured) => {
            if (section.type === "block") {
              // A block references one piece: take its link, its title and its
              // picture, and leave the description for the editor to write.
              onChange({
                url: article.url,
                heading: section.heading?.trim() ? section.heading : article.title,
                image: section.image?.trim() ? section.image : article.image,
              });
            } else {
              setArticles(asFeatured ? [article] : [...articles, article]);
              // Open its editor: the description is what you came here to write.
              setJustAdded(article.id);
            }
            setPicking(false);
          }}
        />
      )}
    </div>
  );
}

/**
 * What a section is still missing before it can be sent, phrased as the action
 * that fixes it. `null` means the section is complete, or draws its content
 * from the subscriber's category and so has nothing to fill in.
 */
function setupNeeded(s: NewsletterSection): string | null {
  const has = (v?: string | null) => Boolean(v && v.trim());
  switch (s.type) {
    case "articles":
    case "featured":
      // A section that neither carries a link nor fills itself sends nothing.
      return (s.articles?.length ?? 0) === 0 && (s.count ?? 0) === 0
        ? "No links added yet"
        : null;
    case "block":
      return has(s.heading) || has(s.text) || has(s.image)
        ? null
        : "Nothing in this block yet";
    case "image":  return has(s.image) ? null : "No photo yet";
    case "banner": return has(s.image) || has(s.heading) ? null : "No headline or photo yet";
    case "cta":    return has(s.url) ? null : "Button has no link yet";
    case "doctor": return has(s.text) ? null : "No recommendation written yet";
    case "tip":    return has(s.text) ? null : "No tip written yet";
    case "text":   return has(s.text) ? null : "No text written yet";
    default:       return null;
  }
}

/**
 * What the reader would see in a section that is not finished yet: nothing.
 *
 * This used to be a dashed "＋ Add a photo" button, which said the same thing
 * as the action row beneath the card. One control, one place.
 */
function SetupPrompt({ label }: { label: string; disabled?: boolean; onClick?: () => void }) {
  return <div className="nl-sec-copy is-empty">{label}</div>;
}

/** A settings field with room for an explanation under the label. */
function SettingField({
  label, hint, children,
}: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="nl-field nl-field-explained">
      <span>{label}</span>
      {children}
      {hint && <em className="nl-field-hint">{hint}</em>}
    </label>
  );
}

/**
 * A photo the editor has pointed at, with the one outcome they need to see:
 * whether the address actually resolves. A broken image would otherwise be an
 * empty strip, which reads as "saved fine".
 */
function ImagePreview({ src, alt, bare }: { src: string; alt: string; bare?: boolean }) {
  const [broken, setBroken] = useState(false);
  // A corrected address should get another chance.
  useEffect(() => setBroken(false), [src]);

  if (broken) {
    return (
      <div className="nl-sec-imgfail">
        Nothing loads from that address. Check it opens in a browser, and that it
        is the image itself rather than the page it sits on.
      </div>
    );
  }
  const img = <img src={src} alt={alt} aria-hidden={alt ? undefined : true}
    onError={() => setBroken(true)} />;
  return bare ? img : <div className="nl-sec-banner">{img}</div>;
}

/**
 * What you can do to a section, named on the card.
 *
 * `pick` opens the article chooser; everything else opens the settings
 * drawer, which is where the field lives.
 */
type SectionAction = { label: string; icon: keyof typeof ACTION_ICON; does: "pick" | "edit"; field?: string };

function sectionActions(s: NewsletterSection): SectionAction[] {
  const has = (v?: string | null) => Boolean(v && v.trim());
  switch (s.type) {
    // A featured section holds one article, so its picker replaces rather
    // than appends — the labels have to say which.
    case "featured":
      return [
        { label: (s.articles?.length ?? 0) ? "Change the article" : "Choose an article", icon: "pick", does: "pick" },
        { label: "Settings", icon: "settings", does: "edit", field: "category" },
      ];
    case "articles":
      return [
        { label: (s.articles?.length ?? 0) ? "Add another article" : "Choose an article", icon: "pick", does: "pick" },
        { label: "Settings", icon: "settings", does: "edit", field: "category" },
      ];
    case "category":
      return [{ label: "Edit heading", icon: "edit", does: "edit", field: "heading" }];
    case "block":
      return [
        { label: has(s.url) ? "Change the article" : "Use an article or term", icon: "pick", does: "pick" },
        { label: has(s.image) ? "Change photo" : "Add a photo", icon: "image", does: "edit", field: "image" },
        { label: has(s.heading) ? "Edit heading" : "Add a heading", icon: "edit", does: "edit", field: "heading" },
        { label: has(s.text) ? "Edit description" : "Add a description", icon: "text", does: "edit", field: "text" },
        { label: has(s.url) ? "Edit link" : "Add a link", icon: "link", does: "edit", field: "url" },
      ];
    case "banner":
      return [
        { label: has(s.image) ? "Change photo" : "Add a photo", icon: "image", does: "edit", field: "image" },
        { label: has(s.heading) ? "Edit headline" : "Add a headline", icon: "edit", does: "edit", field: "heading" },
        { label: has(s.url) ? "Edit link" : "Add a link", icon: "link", does: "edit", field: "url" },
      ];
    case "image":
      return [{ label: has(s.image) ? "Change photo" : "Add a photo", icon: "image", does: "edit", field: "image" }];
    case "cta":
      return [
        { label: "Edit button text", icon: "edit", does: "edit", field: "cta_label" },
        { label: has(s.url) ? "Edit link" : "Add a link", icon: "link", does: "edit", field: "url" },
      ];
    case "doctor":
      return [
        { label: has(s.text) ? "Edit the note" : "Write the note", icon: "edit", does: "edit", field: "text" },
        { label: has(s.url) ? "Edit link" : "Add a link", icon: "link", does: "edit", field: "url" },
      ];
    case "tip":
    case "text":
      return [{ label: has(s.text) ? "Edit the text" : "Write the text", icon: "edit", does: "edit", field: "text" }];
    default:
      return [];
  }
}

/**
 * The block as the reader will meet it, in miniature.
 *
 * A layout is a spatial decision, so the card shows the arrangement rather
 * than naming it — an editor comparing "picture beside text" with "picture on
 * top" should be able to see the difference without sending themselves a test.
 */
function BlockPreview({
  section, missing,
}: { section: NewsletterSection; missing: string | null }) {
  const layout = (section.layout as string) ?? "stacked";
  const heading = section.heading?.trim();
  const text = section.text?.trim();

  if (missing) return <div className="nl-sec-copy is-empty">{missing}</div>;

  const picture = section.image
    ? <img className="nl-blockprev-img" src={section.image} alt=""
        onError={(e) => { e.currentTarget.dataset.broken = "true"; }} />
    : <div className="nl-blockprev-img is-placeholder" aria-hidden />;

  const copy = (
    <div className="nl-blockprev-copy">
      {heading && <span className="nl-blockprev-head"><Bidi>{heading}</Bidi></span>}
      {text && <span className="nl-blockprev-text"><Bidi>{text}</Bidi></span>}
      {section.url?.trim() && (
        <span className="nl-blockprev-link">{section.cta_label?.trim() || "اعرف المزيد"}</span>
      )}
    </div>
  );

  if (layout === "overlay") {
    return (
      <div className="nl-blockprev is-overlay">
        {section.image && <img className="nl-blockprev-bg" src={section.image} alt="" />}
        <div className="nl-blockprev-overlaycopy">{copy}</div>
      </div>
    );
  }

  if (layout === "text" || !section.image) {
    return <div className="nl-blockprev is-text">{copy}</div>;
  }

  const pictureLast = layout === "image-end" || layout === "image-below";
  return (
    <div className={`nl-blockprev is-${layout}`}>
      {pictureLast ? <>{copy}{picture}</> : <>{picture}{copy}</>}
    </div>
  );
}
